/**
 * @file configService.js
 * @description Platform configuration service. Provides a thin async wrapper around
 *   the platform_config table with an in-memory cache, validation, and graceful
 *   degradation to hardcoded defaults when the database is unavailable.
 * @author Victor Carpinelli
 * @version 1.0.0
 * @module lib/configService
 */
// ============================================================
// ABA cases — Platform Configuration Service
//
// Thin async wrapper around the `platform_config` table.
// Provides an in-memory cache so components can call getConfig()
// freely without hammering the database.
//
// Graceful degradation: if Supabase is unavailable (dev / offline),
// all reads fall back to CONFIG_DEFAULTS so the UI never hard-crashes.
// ============================================================

import { supabase } from "./supabase";
import { logAdminAction, AUDIT } from "./adminAuditService";
import { logCaughtError } from "./errorService";

// ------------------------------------------------------------
// Hardcoded defaults — mirrors the seed rows in the migration.
// Used as fallback when the DB is unreachable or a key is absent.
// ------------------------------------------------------------
const CONFIG_DEFAULTS = {
  bcaba_restricted_states: ["CA","FL","TX","NY","NJ","OH","PA","MA","IL","GA","CO","AZ","WA","NC","MI"],
  bcaba_warn_days: 60,
  rbt_warn_days: 30,
  bcba_warn_days: 60,
  case_stale_days: 30,           // legacy global fallback — still read by old code paths
  // Per-credential-type freshness windows (STRAT-R7-3)
  // RBT: fast-moving market — standard 14d/30d cadence
  freshness_window_rbt:   14,
  stale_window_rbt:       30,
  // BCBA: scarce supply — keep visible longer before going stale
  freshness_window_bcba:  21,
  stale_window_bcba:      60,
  // BCaBA: similar supply dynamics to RBT
  freshness_window_bcaba: 14,
  stale_window_bcaba:     30,
  // Note: `urgency_boost_price` ($29) and `featured_case_price` ($49) were
  // removed in favor of the unified credit-based monetization model. Urgency
  // is now bought via boost_urgency_badge_credits (see below); there is no
  // separate "Featured Case" product — Spotlight covers top-of-search lift.
  // Must match the live platform_config row (2) — this is only the fallback
  // used before that row loads (e.g. during prerendering, where Supabase
  // calls are deliberately aborted; see scripts/prerender.js). A stale value
  // here doesn't affect a hydrated browser, which always shows the real DB
  // value, but it DOES get baked permanently into the static HTML that AI
  // crawlers (GPTBot, ClaudeBot, PerplexityBot, etc.) read verbatim — they
  // don't run JS, so they only ever see this fallback. Confirmed live
  // 2026-09-23: ChatGPT reported "3 free cases" reading abacases.com, traced
  // to exactly this default being stale after the real value became 2.
  max_free_cases_agency: 2,
  // Starter's Initial Promo cutover. Single source of truth for both the
  // real entitlement (public.get_starter_free_cap() reads this live in
  // Postgres — see migration starter_promo_date_gate) and the marketing
  // copy (src/lib/plans.js's getStarterPromoDates()). Before this date,
  // Starter agencies get max_free_cases_agency free slots; on/after it,
  // they get starter_promo_post_cases instead.
  starter_promo_end_date: "2026-12-31",
  starter_promo_post_cases: 0,
  // The promo ends at midnight in THIS time zone at the end of starter_promo_end_date (owner 2026-11-02:
  // midnight America/New_York). One engine computes the instant: DB starter_promo_end_at(), mirrored by
  // getStarterPromoDates() in plans.js.
  starter_promo_end_tz: "America/New_York",
  // Plans from Jan 2027. Starter IS the extra-openings subscription (extra_case_slot_price_starter, quantity N =
  // Starter with its first opening included plus N-1 extra openings). plan_starter_cases is display only.
  plan_starter_cases: 1,
  // Kill switch for the whole new model. OFF (default): every rule, number and gate is exactly today's.
  billing_starter_enabled: false,
  // Plan gate for ABAcases Workforce writes (the Workforce branch reads it through the DB engine).
  // OFF (default): Workforce is open to every agency.
  workforce_plan_gate_enabled: false,
  // ABAcases Workforce released? Public pricing copy names Workforce only when this is on (lib/agencyPricing.js).
  // ONE definition (here) and ONE Admin place (Settings > Monetization). Default false = production today (no row yet).
  // TEMPORARY HOME: the Workforce branch owns every workforce_* key in src/lib/workforceConfig.js (Admin > Settings > Workforce);
  // when it merges, delete this entry, the CONFIG_DEFINITIONS entry below and the Monetization row, so the key is defined once.
  workforce_enabled: false,
  // Workforce roster limit per plan (people tracked in ABAcases Workforce). Shown on public pages only while
  // workforce_enabled is on; the server enforces the same keys. Same temporary home as workforce_enabled above:
  // the Workforce branch defines workforce_max_people_starter / _pro once in src/lib/workforceConfig.js.
  workforce_max_people_starter: 15,
  workforce_max_people_pro: 500,
  // Days live openings of an agency with no plan stay live after the promo ends, then they are paused.
  plan_unpaid_openings_grace_days: 14,
  // Boost credit pack prices (USD). Displayed and charged in Billing.
  credit_pack_1_price: 15,
  credit_pack_5_price: 65,
  credit_pack_20_price: 220,
  credit_pack_50_price: 499,
  // Free plan trial limit in days. 0 = unlimited (current default).
  // When set to a positive number, agencies on the Starter plan see a countdown
  // and an upgrade prompt. Increase gradually as the platform grows.
  starter_free_days: 0,
  // Set to a Slack webhook URL, email relay, or any HTTP endpoint to receive critical error alerts
  alert_webhook_url: null,
  // The notification INBOX only, per plan (see src/lib/retentionService.js and
  // purge_notifications_by_plan()). Pro/founder agencies are never purged.
  // Starter agencies are promised 30 days on the Plans page, so keep >= 30.
  // The legacy single `notification_retention_days` (10) is no longer read.
  notification_retention_days_starter: 30,
  notification_retention_days_default: 30,
  // Business/analytics events: the value is the history. 400 days covers
  // year-over-year comparisons with room to spare. At ~86 events/day and
  // ~0.8 kB/row this costs single-digit MB.
  event_retention_days: 400,
  // Clickstream (search, views, impressions) — roughly three quarters of event
  // volume and worth little after a month. See CLICKSTREAM_EVENTS in
  // src/lib/eventRetention.js for exactly which types this covers.
  event_clickstream_retention_days: 30,
  // Days to retain admin_audit_log entries. HIPAA / BAA §5 guidance: 6 years minimum.
  // Default 2555 = 7 years. Daily cron 'purge-admin-audit-log' enforces this automatically.
  audit_log_retention_days: 2555,
  // Designated Privacy/Security officer email (BAA §5.3). Receives breach SLA warnings
  // and privacy-request overdue alerts. Defaults to support address until explicitly set.
  privacy_security_owner_email: "security.officer@abacases.com",
  // Auto-archive: days after close/fill before a case moves to archive
  auto_archive_days: 30,
  // Days a freshness-paused case sits paused before it archives on its own,
  // closing out any applicants still waiting and freeing its case slot.
  // 0 disables — paused cases then stay paused indefinitely.
  paused_archive_days: 60,
  // Offer SLA: days an offer can sit with no response before the candidate is
  // nudged, and (longer) before the agency is told it has gone unanswered.
  offer_nudge_days: 3,
  offer_stale_days: 10,
  // Boost feature credit costs (admin-configurable). Pricing rationale:
  //   urgency_badge (2 cr, until filled, unlimited)  = persistent self-flag
  //   reach_blast   (2 cr, one-time push)            = activation push
  //   candidate_expand (2 cr, +10mi for 14d)         = audience widener
  //   spotlight     (3 cr, top-of-search 7d, capped) = scarce marketing buy
  //   bundle        (6 cr — saves 1 vs 7 separate)   = combined attack
  boost_urgency_badge_credits:    2,
  boost_candidate_expand_credits: 2,
  boost_reach_blast_credits:      2,
  boost_spotlight_credits:        3,
  boost_bundle_credits:           5,
  // Agency-direct-invite — paid action; same canonical key the
  // send_paid_agency_invite RPC reads server-side. UI displays the
  // value via getBoostProducts(...).agency_invite.credits, so admin
  // edits flow through to both the modal CTA label and the actual
  // server-side debit without code changes.
  boost_agency_invite_credits:    3,
  // Cross-state Direct Invite premium — charged by send_paid_agency_invite
  // when the case state ≠ therapist state (the cross_state flag it already
  // stamps). Prices ACCESS to out-of-state supply you can't reach locally.
  // Tunable from the admin Control Center; start lower and let the
  // cross_state response-rate data set the final premium.
  boost_agency_invite_cross_state_credits: 8,
  // Free same-state agency invites per calendar month (send_paid_agency_invite reads the platform_config rows; migration
  // 20261120090000 seeds 10 and -1). -1 = unlimited. Admin > Settings listed these keys without a definition, so every Save failed.
  agency_free_invites_monthly_starter: 10,
  agency_free_invites_monthly_pro: -1,
  // ── Plan pricing (admin-controlled) ─────────────────────────────────────
  // Fallbacks only: the live values come from platform_config (Admin). Keep these equal to the
  // admin values so a page rendered before config loads never shows an old price.
  plan_pro_monthly: 179,
  plan_pro_annual: 150,
  // Seasonal Pause monthly price — paid hibernate. Listings hidden, history
  // preserved. Used by stripe-pause-subscription to swap the Stripe sub
  // to this variant when an agency hibernates.
  plan_hibernate_monthly: 9,
  plan_annual_discount_pct: 20,
  plan_pro_cases: 10,
  // Legacy — Pro plan no longer bundles monthly boost credits. Default 0;
  // setting this >0 from admin Control Center re-enables the monthly grant
  // (escape hatch). The boost product is now sold as packs only, with a
  // one-time onboarding taste via boost_signup_grant_credits.
  plan_pro_credits: 0,
  // One-time grant when an agency completes onboarding (profile_complete
  // flips true). Lets them try Spotlight on their first case before paying.
  // 0 = disabled.
  boost_signup_grant_credits: 3,
  // Recurring monthly price for an extra case listing slot — now PLAN-
  // DIFFERENTIATED. Starter pays more than Pro per extra slot so that an
  // agency needing more capacity leans toward upgrading to Pro (whose extra
  // slots are cheaper) rather than stacking pricey slots on the free tier.
  // Pro's slot price should stay ~ plan_pro_monthly / plan_pro_cases ($/slot)
  // so extras never undercut the base plan. Read via mrr.slotPriceFor(plan).
  extra_case_slot_price_starter: 29,
  extra_case_slot_price_pro: 15,
  // DEPRECATED — superseded by the per-plan keys above. Retained only as a
  // last-resort fallback for old DB rows / RPCs; not shown in the admin UI.
  extra_case_slot_price: 9,
  // Master toggle for production billing. When true, Billing.jsx routes the
  // subscribe button through Stripe Checkout and the change_plan RPC accepts
  // non-test-mode plan changes. When false, the system operates in test mode:
  // local-only plan changes, no Stripe Checkout redirect. Flip via Control
  // Center; the change_plan RPC reads this same key server-side as a gate.
  billing_live: false,
  // ── Stripe connection (admin-tunable, public values) ────────────────────
  // The Stripe publishable key — public by design (Stripe calls it
  // "publishable"), so it's safe in the client bundle. Stored here instead
  // of env vars so it's admin-rotatable without a rebuild. The pk_live_
  // value should only be set when billing_live=true; the admin UI surfaces
  // a mismatch warning if not.
  // Secret keys (STRIPE_SECRET_KEY, STRIPE_WEBHOOK_SECRET) MUST live in
  // Supabase Function Secrets — never in this table.
  stripe_publishable_key: null,
  // Base URL used by stripe-create-checkout as the default for
  // success/cancel/return URLs when the client doesn't override. Set this
  // to the deployed app's origin (e.g. https://abacases.com or your
  // preview URL). Falls back to env var SITE_URL on the edge function if
  // empty.
  stripe_site_url: null,
  // Enable Stripe Tax auto-calculation on every Checkout. Only flip ON
  // after completing Stripe Tax registration in the Stripe Dashboard —
  // turning it on prematurely causes session_create_failed errors.
  // Edge function reads this from platform_config first; falls back to
  // env var STRIPE_AUTOMATIC_TAX for backwards compat.
  stripe_automatic_tax: false,
  // Per-recipient outreach pricing — variable cost so neither product
  // cannibalizes the other. Same rate keeps the choice workflow-driven.
  direct_outreach_credits_per_recipient: 1,
  reach_blast_credits_per_recipient:     1,
  // Remote (nationwide) Reach Blast — flat per-blast price + recipient cap, so
  // the cost can't ride up when the "radius" is the whole country (Slice 2b).
  reach_blast_remote_flat_credits:       12,
  reach_blast_remote_recipient_cap:      15,
  // Supply-side spam protection: max cross-state outreach contacts a single
  // therapist receives across ALL agencies in the rolling window.
  cross_state_contact_cap:               3,
  cross_state_contact_window_days:       14,
  // Support email shown in "Contact us" link
  support_email: "support@abacases.com",
  // Spotlight area cap: max spotlighted cases per geographic area. 0 = unlimited.
  spotlight_max_per_area: 3,
  // Agency posting gate: require NPI verification before an agency can post cases.
  // true (default) = NPI required; false = agencies without NPI can post freely.
  require_npi_to_post: true,
  // Agency posting gate: require a signed HIPAA BAA before an agency can post.
  // true (default) = BAA required; false = agencies without a BAA on file can post.
  require_baa_to_post: true,
  // ── Email & Authentication ───────────────────────────────────────────────
  // Toggle email verification on signup. When false, users skip the verify step.
  // Guest access to the /cases-v2 finder. FALSE = logged-out visitors get the
  // signup gate instead of a browsable map. Kept false while case supply is
  // being built: an empty marketplace shown to strangers costs more than it
  // earns. Flip true once there is real local inventory to browse.
  public_case_browsing: false,
  require_email_verification: false,
  // Hours before the verification link expires. Recommend 2–24.
  email_verification_hours: 2,
  // Master switch for the Monday weekly digest (therapists + agencies).
  // Default ON; turn OFF when a digest would have nothing true to say.
  weekly_digest_enabled: true,
  // Send the admin a notification email whenever a new user joins.
  notify_admin_on_signup: true,
  // Email address that receives admin signup notifications. Leave blank to disable.
  admin_notification_email: "",
  // Sender display name used in all outbound emails.
  email_from_name: "ABAcases",
  // Sending address. Must be a verified domain in Resend.
  email_from_address: "notifications@abacases.com",
  // Optional reply-to address. Leave blank to use the from address.
  email_reply_to: "",
  // Unread message emails (send-unread-message-emails). Seeded true by 20261014090000_unread_message_emails.sql.
  unread_message_email_enabled: true,
  // Credential expiry emails (notify-credential-expiry). OFF until the owner turns it on; the in-app bell is separate.
  credential_expiry_emails_enabled: false,
  // ── Account Deletion ────────────────────────────────────────────────────
  // Grace period in days for self-initiated deletion requests.
  deletion_grace_days_self: 30,
  // Grace period in days for admin-initiated deletion requests.
  deletion_grace_days_admin: 7,
  // Email sent to user immediately after they request deletion.
  // Use {{name}}, {{grace_days}}, {{deadline}}, {{reactivation_link}} as placeholders.
  deletion_email_request: "Hi {{name}},\n\nYour account deletion request has been received. Your account will be permanently deleted in {{grace_days}} days ({{deadline}}).\n\nIf you change your mind, you can reactivate your account by clicking the link below before the deadline:\n\n{{reactivation_link}}\n\nAfter {{deadline}}, your account and all associated data will be permanently removed.\n\nABAcases Team",
  // Email sent when the user clicks the reactivation link successfully.
  deletion_email_reactivated: "Hi {{name}},\n\nGreat news — your account has been successfully reactivated. You can log in at any time.\n\nIf you did not request this reactivation, please contact support immediately at support@abacases.com.\n\nABAcases Team",
  // Email sent when the grace period expires and data is permanently purged.
  deletion_email_purged: "Hi,\n\nThis is a confirmation that your ABAcases account and all associated data have been permanently deleted as requested.\n\nIf you have any questions, contact support@abacases.com.\n\nABAcases Team",
  // Message shown on the signup page when a blocked email tries to register.
  deletion_blocked_email_message: "This email address is no longer eligible for registration. Please contact support@abacases.com if you believe this is an error.",
  // ── Agency Suspension ────────────────────────────────────────────────────
  // Email sent to the agency when their account is suspended by admin.
  // Use {{name}}, {{admin_notes}}, {{site_url}} as placeholders.
  suspension_email_body: "Hi {{name}},\n\nYour ABAcases agency account has been suspended by our team.\n\n{{admin_notes}}\nYour active openings have been removed from therapist discovery while your account is suspended. Therapists with pending applications have been notified.\n\nIf you believe this is an error or would like to appeal, please contact us at support@abacases.com.\n\nABAcases Team",
  // Email sent to the agency when their suspension is lifted.
  suspension_reactivate_email_body: "Hi {{name}},\n\nGreat news — your ABAcases agency account has been reactivated. Your openings are now visible to therapists again.\n\nIf you have any questions, contact us at support@abacases.com.\n\nABAcases Team",
  // Message displayed on the portal gate screen when a suspended agency tries to log in.
  suspension_portal_message: "Your agency account has been suspended by the platform team. Your openings are not visible to therapists while the suspension is active. Please contact support@abacases.com if you believe this is an error or to appeal.",
  // Message displayed on the portal gate screen when a suspended therapist tries to log in.
  suspension_worker_portal_message: "Your account has been suspended by the platform team. Your profile is not visible to agencies while the suspension is active. Please contact support@abacases.com if you believe this is an error or to appeal.",
};

const CONFIG_DEFINITIONS = {
  bcaba_restricted_states: {
    type: "state-list",
    minItems: 0,
    maxItems: 60,
    label: "BCaBA Restricted States",
    configGroup: "bcaba",
    description: "US states where BCaBA practitioners require additional review.",
  },
  bcaba_warn_days: {
    type: "integer",
    min: 1,
    max: 365,
    label: "BCaBA Credential Warning Days",
    configGroup: "renewal",
    description: "Days before BCaBA credential expiry to show a warning.",
  },
  rbt_warn_days: {
    type: "integer",
    min: 1,
    max: 365,
    label: "RBT Credential Warning Days",
    configGroup: "renewal",
    description: "Days before RBT credential expiry to show a warning.",
  },
  bcba_warn_days: {
    type: "integer",
    min: 1,
    max: 365,
    label: "BCBA Credential Warning Days",
    configGroup: "renewal",
    description: "Days before BCBA credential expiry to show a warning.",
  },
  case_stale_days: {
    type: "integer",
    min: 1,
    max: 365,
    label: "Legacy Case Stale Days",
    configGroup: "cases",
    description: "Legacy global fallback for case staleness.",
  },
  freshness_window_rbt: {
    type: "integer",
    min: 1,
    max: 180,
    label: "RBT Freshness Window",
    configGroup: "cases",
    description: "Days before an RBT case becomes due soon.",
  },
  stale_window_rbt: {
    type: "integer",
    min: 1,
    max: 365,
    label: "RBT Stale Window",
    configGroup: "cases",
    description: "Days before an RBT case becomes stale.",
  },
  freshness_window_bcba: {
    type: "integer",
    min: 1,
    max: 180,
    label: "BCBA Freshness Window",
    configGroup: "cases",
    description: "Days before a BCBA case becomes due soon.",
  },
  stale_window_bcba: {
    type: "integer",
    min: 1,
    max: 365,
    label: "BCBA Stale Window",
    configGroup: "cases",
    description: "Days before a BCBA case becomes stale.",
  },
  freshness_window_bcaba: {
    type: "integer",
    min: 1,
    max: 180,
    label: "BCaBA Freshness Window",
    configGroup: "cases",
    description: "Days before a BCaBA case becomes due soon.",
  },
  stale_window_bcaba: {
    type: "integer",
    min: 1,
    max: 365,
    label: "BCaBA Stale Window",
    configGroup: "cases",
    description: "Days before a BCaBA case becomes stale.",
  },
  urgency_boost_price: {
    type: "integer",
    min: 0,
    max: 10000,
    label: "Urgency Boost Price",
    configGroup: "monetization",
    description: "One-time price for urgency boosting a case.",
  },
  featured_case_price: {
    type: "integer",
    min: 0,
    max: 10000,
    label: "Featured Case Price",
    configGroup: "monetization",
    description: "One-time price for featuring a case.",
  },
  max_free_cases_agency: {
    type: "integer",
    min: 0,
    max: 100,
    label: "Promo Free Openings (until the promo end date)",
    configGroup: "monetization",
    description: "Openings every agency can have free until the Starter Promo End Date. They stack on top of what a paid plan includes while the promo runs.",
  },
  starter_free_days: {
    type: "integer",
    min: 0,
    max: 3650,
    label: "Starter Free Days",
    configGroup: "monetization",
    description: "Number of free trial days for starter agencies. 0 means unlimited.",
  },
  starter_promo_end_date: {
    type: "text",
    placeholder: "YYYY-MM-DD",
    maxLength: 10,
    label: "Starter Promo End Date",
    configGroup: "monetization",
    description: "Last day (inclusive) of the free period; it ends at midnight at the end of this day in Starter Promo End Time Zone. The plan tier engine reads this live in Postgres and the site copy reads it too: this is the only place to move the date. Format: YYYY-MM-DD.",
  },
  starter_promo_end_tz: {
    type: "text",
    maxLength: 64,
    label: "Starter Promo End Time Zone",
    configGroup: "monetization",
    description: "IANA time zone whose midnight ends the promo (end of Starter Promo End Date). Default America/New_York.",
  },
  plan_starter_cases: {
    type: "integer", min: 1, max: 100,
    label: "Starter Plan Included Openings",
    configGroup: "monetization",
    description: "Display only: openings the Starter price includes (the first of the subscription quantity). Starter costs the Starter extra-opening price per opening, so this does not change any amount.",
  },
  billing_starter_enabled: {
    type: "boolean",
    label: "New plan model live (Starter)",
    configGroup: "monetization",
    description: "OFF: every plan rule behaves as before. ON: paid Starter can be bought, agencies with no plan become read-only after the promo end (plus grace), and the DB tier engine drives limits.",
  },
  workforce_plan_gate_enabled: {
    type: "boolean",
    label: "Workforce requires a plan",
    configGroup: "monetization",
    description: "OFF: ABAcases Workforce writes are open to every agency. ON (and the new plan model live): only agencies with a plan, the promo or Seasonal Pause can write.",
  },
  workforce_max_people_starter: {
    type: "integer", min: 1, max: 100000,
    label: "Workforce people limit: Starter",
    configGroup: "monetization",
    description: "Most people an agency on Starter can track in ABAcases Workforce.",
  },
  workforce_max_people_pro: {
    type: "integer", min: 1, max: 100000,
    label: "Workforce people limit: Pro",
    configGroup: "monetization",
    description: "Most people an agency on Pro (or Founder) can track in ABAcases Workforce.",
  },
  // Defined ONCE (see CONFIG_DEFAULTS): the Workforce branch takes ownership in src/lib/workforceConfig.js and this entry goes then.
  workforce_enabled: {
    type: "boolean",
    label: "ABAcases Workforce released",
    configGroup: "monetization",
    description: "Feature switch for ABAcases Workforce. Public pricing pages name Workforce only while this is ON.",
  },
  plan_unpaid_openings_grace_days: {
    type: "integer", min: 0, max: 90,
    label: "Grace Days for Openings With No Plan",
    configGroup: "monetization",
    description: "Days after the promo ends that live openings of an agency with no plan stay live before they are paused (never deleted; restored when a plan is bought).",
  },
  starter_promo_post_cases: {
    type: "integer",
    min: 0,
    max: 100,
    label: "Starter Promo — Post-Cutover Free Openings",
    configGroup: "monetization",
    description: "Free openings left once the promo has ended. Keep 0: from the promo end an agency needs a plan.",
  },
  alert_webhook_url: {
    type: "nullable-string",
    maxLength: 2000,
    label: "Alert Webhook URL",
    configGroup: "platform",
    description: "Optional webhook endpoint for critical platform alerts.",
  },
  notification_retention_days_starter: {
    type: "integer",
    min: 1,
    max: 3650,
    label: "Notification Retention: Starter agencies (days)",
    configGroup: "platform",
    description: "Days to keep in-app notifications for agencies that are not Pro. The Plans page promises Starter 30 days, so do not go below 30. Pro and founder agencies are never purged. Default 30.",
  },
  notification_retention_days_default: {
    type: "integer",
    min: 1,
    max: 3650,
    label: "Notification Retention: providers and admins (days)",
    configGroup: "platform",
    description: "Days to keep in-app notifications for therapists (RBT, BCBA, BCaBA) and admins. Default 30.",
  },
  event_retention_days: {
    type: "integer",
    min: 1,
    max: 3650,
    label: "Event Retention (days)",
    configGroup: "platform",
    description: "Days to keep business and analytics events (applications, posts, hires, boost spend and conversions). Boost ROI compares the last 30 days against the 30 previous, so anything under ~90 makes that comparison impossible. Default 400.",
  },
  event_clickstream_retention_days: {
    type: "integer",
    min: 1,
    max: 3650,
    label: "Clickstream Retention (days)",
    configGroup: "platform",
    description: "Days to keep high-volume browsing telemetry (case views, searches, boost impressions). This is most of the event volume and little of its value. Default 30.",
  },
  auto_archive_days: {
    type: "integer",
    min: 0,
    max: 365,
    label: "Auto-Archive (days after close)",
    configGroup: "platform",
    description: "Days after a case is closed/filled before it is automatically archived. Set to 0 to disable auto-archive. Default 30.",
  },
  paused_archive_days: {
    type: "integer",
    min: 0,
    max: 365,
    label: "Auto-Archive (days after pause)",
    configGroup: "cases",
    description: "Days a freshness-paused case stays paused before it archives, closes any waiting applicants, and frees its slot. Set to 0 to leave paused cases paused indefinitely. Default 60.",
  },
  offer_nudge_days: {
    type: "integer",
    min: 1,
    max: 90,
    label: "Offer Nudge (days)",
    configGroup: "cases",
    description: "Days an offer can go unanswered before the candidate is reminded. Reminders repeat weekly, not daily. Default 3.",
  },
  offer_stale_days: {
    type: "integer",
    min: 1,
    max: 180,
    label: "Offer Unanswered Alert (days)",
    configGroup: "cases",
    description: "Days an offer can go unanswered before the agency is told it has stalled. Should be longer than the candidate nudge. Default 10.",
  },
  boost_urgency_badge_credits: {
    type: "integer", min: 0, max: 50,
    label: "Urgency Badge (credits)",
    configGroup: "monetization",
    description: "Credits required to apply the Urgency Badge boost.",
  },
  boost_candidate_expand_credits: {
    type: "integer", min: 0, max: 50,
    label: "Expand Radius (credits)",
    configGroup: "monetization",
    description: "Credits required to expand match radius by +10 miles for 14 days.",
  },
  boost_reach_blast_credits: {
    type: "integer", min: 0, max: 50,
    label: "Reach Blast (credits)",
    configGroup: "monetization",
    description: "Credits required to push a notification to all uncontacted matched therapists.",
  },
  boost_spotlight_credits: {
    type: "integer", min: 0, max: 50,
    label: "Spotlight (credits)",
    configGroup: "monetization",
    description: "Credits required to pin a listing to top of search for 7 days.",
  },
  boost_bundle_credits: {
    type: "integer", min: 0, max: 50,
    label: "Fast Fill Bundle (credits)",
    configGroup: "monetization",
    description: "Credits required for the Fast Fill Bundle (Spotlight + Reach Blast + Urgency Badge).",
  },
  boost_agency_invite_credits: {
    type: "integer", min: 0, max: 50,
    label: "Agency Invite (credits)",
    configGroup: "monetization",
    description: "Credits required to send a direct invite to a specific therapist for a specific case. Server reads this value at debit time, so changes apply immediately to both the modal CTA label and the actual charge.",
  },
  boost_agency_invite_cross_state_credits: {
    type: "integer", min: 0, max: 50,
    label: "Cross-State Invite (credits)",
    configGroup: "monetization",
    description: "Credits required to direct-invite a therapist in a different state than the case (a remote, cross-state placement). Premium over the local Agency Invite because it reaches out-of-state supply you can't fill locally. Server reads this at debit time via the cross_state flag, so the modal CTA label and the actual charge always match. If cross-state invites convert worse than local, lower this toward the local price.",
  },
  credit_pack_1_price: {
    type: "integer", min: 0, max: 10000,
    label: "1-Credit Pack Price ($)",
    configGroup: "monetization",
    description: "Price agencies pay for 1 boost credit.",
  },
  credit_pack_5_price: {
    type: "integer", min: 0, max: 10000,
    label: "5-Credit Pack Price ($)",
    configGroup: "monetization",
    description: "Price agencies pay for 5 boost credits (bulk discount).",
  },
  credit_pack_20_price: {
    type: "integer", min: 0, max: 10000,
    label: "20-Credit Pack Price ($)",
    configGroup: "monetization",
    description: "Price agencies pay for 20 boost credits (bulk discount).",
  },
  credit_pack_50_price: {
    type: "integer", min: 0, max: 10000,
    label: "50-Credit Pack Price ($)",
    configGroup: "monetization",
    description: "Price agencies pay for 50 boost credits (bulk discount).",
  },
  // ── Plan pricing ────────────────────────────────────────────────────────
  plan_pro_monthly: {
    type: "integer", min: 0, max: 999,
    label: "Pro Plan — Monthly ($)",
    configGroup: "plans",
    description: "Monthly price for the Pro plan.",
  },
  plan_pro_annual: {
    type: "integer", min: 0, max: 999,
    label: "Pro Plan — Annual ($/mo)",
    configGroup: "plans",
    description: "Per-month price when billed annually for Pro.",
  },
  plan_annual_discount_pct: {
    type: "integer", min: 0, max: 50,
    label: "Annual Discount (%)",
    configGroup: "plans",
    description: "Discount percentage shown on the annual billing toggle badge.",
  },
  plan_pro_cases: {
    type: "integer", min: 1, max: 100,
    label: "Pro Plan — Included Openings",
    configGroup: "plans",
    description: "Active openings included in the Pro plan.",
  },
  plan_pro_credits: {
    type: "integer", min: 0, max: 50,
    label: "Pro — Monthly Credits (legacy)",
    configGroup: "plans",
    description: "Legacy: monthly boost credits granted to Pro agencies. Default 0 — Pro no longer includes monthly credits. Set >0 only if you want to re-enable the bundle.",
  },
  boost_signup_grant_credits: {
    type: "integer", min: 0, max: 20,
    label: "Signup Boost Grant (credits)",
    configGroup: "monetization",
    description: "One-time credits granted to a new agency when they finish onboarding (3 since 2026-10-19: enough for one Invite to Apply). Set to 0 to disable.",
  },
  extra_case_slot_price_starter: {
    type: "integer", min: 0, max: 1000,
    label: "Starter Price per Opening ($/mo)",
    configGroup: "monetization",
    description: "The Starter price. A Starter subscription of N openings is the Starter plan (first opening included) plus N-1 extra openings, all at this price. Deliberately higher than the Pro extra price to steer agencies toward Pro.",
  },
  extra_case_slot_price_pro: {
    type: "integer", min: 0, max: 1000,
    label: "Extra Opening — Pro ($/mo)",
    configGroup: "monetization",
    description: "Recurring monthly price of each extra opening on Pro, beyond the included openings. Keep about plan_pro_monthly / plan_pro_cases so extras never undercut the base plan.",
  },
  // DEPRECATED — superseded by the per-plan keys above. Kept (with a default)
  // only as a last-resort fallback for old rows/RPCs. Intentionally excluded
  // from the admin Control Center key list so it isn't editable there, but the
  // definition must exist to satisfy the "every default has a definition"
  // invariant (findKeysWithoutDefinition / configService test).
  extra_case_slot_price: {
    type: "integer", min: 0, max: 1000,
    label: "Extra Case Slot — legacy ($/mo)",
    configGroup: "monetization",
    description: "Legacy flat extra-slot price. Superseded by extra_case_slot_price_starter / _pro. Retained as a fallback only.",
  },
  direct_outreach_credits_per_recipient: {
    type: "integer", min: 0, max: 50,
    label: "Direct Outreach (credits per recipient)",
    configGroup: "monetization",
    description: "Per-recipient cost for a Pro agency's curated direct message to a therapist picked from the Cases Map overlay. Total = recipients × this rate.",
  },
  reach_blast_credits_per_recipient: {
    type: "integer", min: 0, max: 50,
    label: "Reach Blast (credits per recipient)",
    configGroup: "monetization",
    description: "Per-recipient cost when an agency triggers a LOCAL Reach Blast on a case (server resolves matched-and-uncontacted therapists within 15 miles). Available to all plans. Total = recipients × this rate. Remote cases use the flat price below instead.",
  },
  reach_blast_remote_flat_credits: {
    type: "integer", min: 0, max: 200,
    label: "Remote Reach Blast (flat credits per blast)",
    configGroup: "monetization",
    description: "Flat cost for ONE nationwide Reach Blast on a remote case — charged once, not per recipient, regardless of how many remote therapists are reached (up to the cap). Flat because a per-recipient price would run away when the radius is the whole country. Start lower and let cross-state response data set the final number.",
  },
  reach_blast_remote_recipient_cap: {
    type: "integer", min: 1, max: 100,
    label: "Remote Reach Blast (recipient cap)",
    configGroup: "monetization",
    description: "Maximum therapists a single nationwide remote Reach Blast reaches. Licensed-in-state candidates are prioritized within the cap. Keeps the flat price honest and protects therapist supply from mass cross-state blasts.",
  },
  cross_state_contact_cap: {
    type: "integer", min: 1, max: 20,
    label: "Cross-state contact cap (per therapist)",
    configGroup: "monetization",
    description: "Max cross-state outreach contacts a single therapist can receive across ALL agencies within the window below. A remote Reach Blast skips therapists already at this cap. Supply-side spam protection for the highest-value (cross-state) placements.",
  },
  cross_state_contact_window_days: {
    type: "integer", min: 1, max: 90,
    label: "Cross-state contact window (days)",
    configGroup: "monetization",
    description: "Rolling window (in days) for the cross-state contact cap above.",
  },
  agency_free_invites_monthly_starter: {
    type: "integer", min: 0, max: 1000,
    label: "Free invites per month (Starter)",
    configGroup: "monetization",
    description: "Same-state invites a Starter agency can send each calendar month without credits. Past it, the Agency Invite credit price applies. 0 turns free invites off for Starter.",
  },
  agency_free_invites_monthly_pro: {
    type: "integer", min: -1, max: 1000,
    label: "Free invites per month (Pro)",
    configGroup: "monetization",
    description: "Same-state invites a Pro agency can send each month without credits. -1 = unlimited.",
  },
  support_email: {
    type: "text",
    label: "Support Email",
    configGroup: "platform",
    description: "Email address shown in the 'Contact us' support link.",
  },
  privacy_security_owner_email: {
    type: "text",
    label: "Privacy & Security owner email",
    configGroup: "compliance",
    description: "Designated privacy/security officer (BAA §5.3). Referenced in breach incident SLA warnings and privacy-request overdue alerts. Defaults to support email until explicitly set.",
  },
  audit_log_retention_days: {
    type: "integer",
    min: 365, max: 3650,
    label: "Audit log retention (days)",
    configGroup: "compliance",
    description: "Days to keep admin audit log entries. HIPAA/BAA §5 minimum: 6 years (2190). Default: 7 years (2555). Daily purge-admin-audit-log cron enforces.",
  },
  plan_hibernate_monthly: {
    type: "integer",
    min: 0, max: 999,
    label: "Hibernate plan monthly price ($)",
    configGroup: "monetization",
    description: "Monthly price for the Seasonal Pause / Hibernate plan. Used by Stripe Sync to mirror into a recurring Stripe Price.",
  },
  // ── Stripe connection keys ────────────────────────────────────────────
  // Edited via the Stripe Connection card, but still must be in the
  // definitions map so the standard setConfig setter accepts them.
  billing_live: {
    type: "boolean",
    label: "Billing live mode",
    configGroup: "stripe",
    description: "Master toggle: when ON, Stripe Checkout uses live keys and real charges occur. OFF keeps everything in test mode.",
  },
  stripe_publishable_key: {
    type: "text",
    label: "Stripe publishable key",
    configGroup: "stripe",
    description: "Public key (pk_live_... or pk_test_...) loaded by the front-end Stripe SDK.",
  },
  stripe_site_url: {
    type: "text",
    label: "Stripe return URL base",
    configGroup: "stripe",
    description: "Origin used to build success/cancel/return URLs for Stripe Checkout sessions and the Customer Portal. Defaults to the request origin when blank.",
  },
  stripe_automatic_tax: {
    type: "boolean",
    label: "Automatic tax (Stripe)",
    configGroup: "stripe",
    description: "Enable Stripe Tax for automatic VAT/sales-tax calculation. Requires Stripe Tax to be activated in the dashboard.",
  },
  spotlight_max_per_area: {
    type: "integer",
    min: 0,
    max: 20,
    label: "Spotlight slots per area (0 = unlimited)",
    configGroup: "monetization",
    description: "Maximum number of spotlighted cases allowed per geographic area. Set to 0 for unlimited.",
  },
  require_npi_to_post: {
    type: "boolean",
    label: "Require NPI to Post",
    configGroup: "platform",
    description: "Require NPI verification before an agency can post cases.",
  },
  require_baa_to_post: {
    type: "boolean",
    label: "Require BAA to Post",
    configGroup: "platform",
    description: "Require a signed HIPAA Business Associate Agreement on file before an agency can post cases. HIPAA compliance gate; enforced server-side.",
  },
  // ── Email & Authentication ───────────────────────────────────────────────
  public_case_browsing: {
    type: "boolean",
    label: "Public case browsing (logged-out)",
    configGroup: "platform",
    description: "When ON, logged-out visitors can browse the case finder. When OFF they get a signup gate instead. Keep OFF while case supply is thin — showing strangers an empty map loses them for good.",
  },
  require_email_verification: {
    type: "boolean",
    label: "Require Email Verification",
    configGroup: "email",
    description: "When enabled, new users must verify their email before accessing the platform.",
  },
  email_verification_hours: {
    type: "number",
    min: 1,
    max: 72,
    label: "Verification Link Expiry (hours)",
    configGroup: "email",
    description: "How many hours the email verification link stays active. Recommended: 2–24.",
  },
  email_from_name: {
    type: "text",
    maxLength: 100,
    label: "Email Sender Name",
    configGroup: "email",
    description: "Display name shown as the sender in all outbound emails.",
  },
  email_from_address: {
    type: "text",
    maxLength: 200,
    label: "Email From Address",
    configGroup: "email",
    description: "Sending email address. Must be verified in Resend.",
  },
  email_reply_to: {
    type: "nullable-string",
    maxLength: 200,
    label: "Reply-To Address (optional)",
    configGroup: "email",
    description: "Optional reply-to address for outbound emails. Leave blank to use the from address.",
  },
  unread_message_email_enabled: {
    type: "boolean",
    label: "Unread message emails",
    configGroup: "email",
    description: "Master switch for the 30 minute sweep that emails people who have unread inbox messages (at most one email per conversation per day).",
  },
  credential_expiry_emails_enabled: {
    type: "boolean",
    label: "Credential expiry emails",
    configGroup: "email",
    description: "Email providers when a verified credential is about to expire (once in the warning window, once in the final week) and once within 7 days after it lapses. OFF by default. Old lapses are never emailed. The in-app bell is separate and always on.",
  },
  weekly_digest_enabled: {
    type: "boolean",
    label: "Weekly digest emails",
    configGroup: "email",
    description: "Master switch for the Monday weekly summary to therapists and agencies. Turn OFF when a digest has nothing true to report — an empty digest that also points people at stale applications costs more than sending nothing.",
  },
  notify_admin_on_signup: {
    type: "boolean",
    label: "Admin signup notifications",
    configGroup: "email",
    description: "Send an email to the admin notification address whenever a new agency or worker creates an account.",
  },
  admin_notification_email: {
    type: "text",
    maxLength: 200,
    label: "Admin notification email",
    configGroup: "email",
    description: "Email address that receives admin notifications (new signups, etc.). Leave blank to disable even when the toggle is on.",
  },
  // ── Account Deletion ────────────────────────────────────────────────────
  deletion_grace_days_self: {
    type: "integer", min: 1, max: 90,
    label: "Self-deletion grace period (days)",
    configGroup: "deletion",
    description: "Days before a user-initiated deletion is permanently purged. Recommended: 30.",
  },
  deletion_grace_days_admin: {
    type: "integer", min: 1, max: 30,
    label: "Admin-deletion grace period (days)",
    configGroup: "deletion",
    description: "Days before an admin-initiated deletion is permanently purged. Recommended: 7.",
  },
  deletion_email_request: {
    type: "long-text",
    maxLength: 2000,
    label: "Deletion request email body",
    configGroup: "deletion",
    description: "Email sent to user on deletion request. Placeholders: {{name}}, {{grace_days}}, {{deadline}}, {{reactivation_link}}.",
  },
  deletion_email_reactivated: {
    type: "long-text",
    maxLength: 2000,
    label: "Reactivation confirmation email body",
    configGroup: "deletion",
    description: "Email sent to user when they successfully reactivate their account.",
  },
  deletion_email_purged: {
    type: "long-text",
    maxLength: 2000,
    label: "Purge confirmation email body",
    configGroup: "deletion",
    description: "Email sent to user after their data has been permanently purged.",
  },
  deletion_blocked_email_message: {
    type: "text",
    maxLength: 500,
    label: "Blocked email signup message",
    configGroup: "deletion",
    description: "Message shown on the signup page when a blocked email tries to register.",
  },
  // ── Agency Suspension ────────────────────────────────────────────────────
  suspension_email_body: {
    type: "long-text",
    maxLength: 2000,
    label: "Suspension notification email",
    configGroup: "suspension",
    description: "Email sent to the agency when admin suspends their account. Placeholders: {{name}}, {{admin_notes}}, {{site_url}}.",
  },
  suspension_reactivate_email_body: {
    type: "long-text",
    maxLength: 2000,
    label: "Reactivation email",
    configGroup: "suspension",
    description: "Email sent to the agency when their suspension is lifted.",
  },
  suspension_portal_message: {
    type: "long-text",
    maxLength: 1000,
    label: "Portal gate message (agencies)",
    configGroup: "suspension",
    description: "Message displayed on the login gate when a suspended agency tries to access the platform.",
  },
  suspension_worker_portal_message: {
    type: "long-text",
    maxLength: 1000,
    label: "Portal gate message (therapists)",
    configGroup: "suspension",
    description: "Message displayed on the login gate when a suspended therapist (RBT/BCBA/BCaBA) tries to access the platform.",
  },
};

function _assertKnownKey(key) {
  if (!Object.prototype.hasOwnProperty.call(CONFIG_DEFINITIONS, key)) {
    throw new Error(`Unknown config key: ${key}`);
  }
}

// ── Schema invariant: every default must have a definition ──────────────
// CONFIG_DEFAULTS provides the fallback value used when a row is missing
// from platform_config; CONFIG_DEFINITIONS provides the validation + UI
// metadata used by the admin SettingsRow. Earlier, several keys had
// defaults but no matching definition — the SettingsRow rendered, the
// admin typed a value, save called setConfig → _assertKnownKey → THROW
// "Unknown config key: …". That ended a session more than once.
//
// The helper below is exported so the test in __tests__/configService can
// guard the invariant at CI time. We also emit a one-shot console warning
// at module load so the gap surfaces immediately in dev consoles.
export function findKeysWithoutDefinition() {
  return Object.keys(CONFIG_DEFAULTS).filter(
    (k) => !Object.prototype.hasOwnProperty.call(CONFIG_DEFINITIONS, k),
  );
}

(function _warnOnSchemaDrift() {
  const orphans = findKeysWithoutDefinition();
  if (orphans.length === 0) return;
  // eslint-disable-next-line no-console
  console.warn(
    `[configService] ${orphans.length} key(s) have a default but no CONFIG_DEFINITIONS entry — ` +
    `saves through the admin Settings UI will throw "Unknown config key": ${orphans.join(", ")}`,
  );
})();

function _normalizeStateList(value) {
  if (!Array.isArray(value)) {
    throw new Error("Expected a list of 2-letter state codes.");
  }
  const normalized = [...new Set(
    value
      .map((item) => String(item || "").trim().toUpperCase())
      .filter(Boolean)
  )].sort();

  if (normalized.some((state) => !/^[A-Z]{2}$/.test(state))) {
    throw new Error("State lists must contain only 2-letter uppercase state codes.");
  }

  return normalized;
}

function _normalizeValue(key, value) {
  _assertKnownKey(key);
  const def = CONFIG_DEFINITIONS[key];

  if (def.type === "boolean") {
    if (typeof value === "boolean") return value;
    if (value === "true"  || value === 1 || value === "1") return true;
    if (value === "false" || value === 0 || value === "0") return false;
    throw new Error(`${key} must be true or false.`);
  }

  if (def.type === "number") {
    const parsed = Number(value);
    if (!Number.isFinite(parsed)) {
      throw new Error(`${key} must be a number.`);
    }
    if (def.min !== undefined && parsed < def.min) {
      throw new Error(`${key} must be at least ${def.min}.`);
    }
    if (def.max !== undefined && parsed > def.max) {
      throw new Error(`${key} must be at most ${def.max}.`);
    }
    return parsed;
  }

  if (def.type === "integer") {
    const parsed = Number(value);
    if (!Number.isInteger(parsed)) {
      throw new Error(`${key} must be a whole number.`);
    }
    // Guard each bound independently — comparing against an undefined min/max
    // is always false, which would let the range check pass silently (fail
    // open) for any definition missing a bound.
    if (def.min != null && parsed < def.min) {
      throw new Error(`${key} must be at least ${def.min}.`);
    }
    if (def.max != null && parsed > def.max) {
      throw new Error(`${key} must be at most ${def.max}.`);
    }
    return parsed;
  }

  if (def.type === "state-list") {
    const normalized = _normalizeStateList(value);
    if (normalized.length < def.minItems || normalized.length > def.maxItems) {
      throw new Error(`${key} must contain between ${def.minItems} and ${def.maxItems} states.`);
    }
    return normalized;
  }

  if (def.type === "nullable-string") {
    if (value == null || String(value).trim() === "") return null;
    const normalized = String(value).trim();
    if (normalized.length > def.maxLength) {
      throw new Error(`${key} must be ${def.maxLength} characters or fewer.`);
    }
    return normalized;
  }

  if (def.type === "text" || def.type === "long-text") {
    const normalized = String(value ?? "").trim();
    if (def.maxLength && normalized.length > def.maxLength) {
      throw new Error(`${key} must be ${def.maxLength} characters or fewer.`);
    }
    return normalized;
  }

  throw new Error(`Unsupported config definition for key: ${key}`);
}

function _validateWindowPair(values, label, freshnessKey, staleKey) {
  const freshness = Number(values[freshnessKey]);
  const stale = Number(values[staleKey]);
  if (!Number.isFinite(freshness) || !Number.isFinite(stale)) return;
  if (freshness > stale) {
    throw new Error(`${label}: due soon threshold cannot be greater than stale threshold.`);
  }
}

function _validateRelationships(nextValues) {
  _validateWindowPair(nextValues, "RBT cases", "freshness_window_rbt", "stale_window_rbt");
  _validateWindowPair(nextValues, "BCBA cases", "freshness_window_bcba", "stale_window_bcba");
  _validateWindowPair(nextValues, "BCaBA cases", "freshness_window_bcaba", "stale_window_bcaba");
  // The candidate gets the first word on their own offer. Alerting the agency
  // that an offer "stalled" before the candidate has even been reminded would
  // have us chasing the wrong person.
  // Keeping noise longer than signal is always a mistake, and usually a typo.
  const eventDays = Number(nextValues.event_retention_days);
  const clickDays = Number(nextValues.event_clickstream_retention_days);
  if (Number.isFinite(eventDays) && Number.isFinite(clickDays) && clickDays > eventDays) {
    throw new Error("Events: clickstream retention cannot be longer than business event retention.");
  }
  const nudge = Number(nextValues.offer_nudge_days);
  const stale = Number(nextValues.offer_stale_days);
  if (Number.isFinite(nudge) && Number.isFinite(stale) && nudge > stale) {
    throw new Error("Offers: candidate nudge cannot come later than the agency stalled-offer alert.");
  }
}

// ------------------------------------------------------------
// Module-level cache: key (string) → parsed JS value (any)
// Populated lazily on first getConfig() call or explicitly via
// loadAllConfigs(). Null signals "not yet loaded".
// ------------------------------------------------------------
let _cache = null; // null = not loaded; Map once populated
// Bumped after every successful loadAllConfigs(), so pages that read prices at render time can re-render
// with the admin values (useConfigVersion). Without it a page rendered before the fetch kept the defaults.
let _version = 0;
const _listeners = new Set();
export function getConfigVersion() { return _version; }
export function onConfigLoaded(fn) { _listeners.add(fn); return () => _listeners.delete(fn); }

function _ensureCache() {
  if (!(_cache instanceof Map)) {
    _cache = new Map();
  }
  return _cache;
}

// ------------------------------------------------------------
// loadAllConfigs({ strict })
//
// Fetches every row from platform_config, parses JSON values,
// and stores them in the cache. Returns the populated Map.
//
// Call this once at app startup (e.g. in App.js) if you want
// synchronous access via getCachedConfig() immediately after.
//
// When the database cannot be read the failure is logged ONCE to Admin > Logs and the cache is filled with
// CONFIG_DEFAULTS, so public pages keep rendering. That is fine for display, but it must never be mistaken
// for the stored values: screens where an admin EDITS settings pass { strict: true } and get the error
// thrown instead, so they can show it and refuse to save defaults over real settings.
// ------------------------------------------------------------
let _loadFailureLogged = false;

export async function loadAllConfigs({ strict = false } = {}) {
  const cache = _ensureCache();

  const { data, error } = await supabase
    .from("platform_config")
    .select("key, value");

  if (error) {
    // One Error object is logged and thrown, so a caller that logs it again is deduped (errorService).
    const failure = error instanceof Error ? error : Object.assign(new Error(error.message || "Could not load settings"), { code: error.code });
    if (!_loadFailureLogged) {
      _loadFailureLogged = true;
      logCaughtError(failure, "config_load_failed");
    }
    console.warn("[configService] loadAllConfigs failed, using defaults:", failure.message);
    if (strict) throw failure;
    // Pre-populate cache with defaults so getCachedConfig() works offline
    for (const [k, v] of Object.entries(CONFIG_DEFAULTS)) {
      if (!cache.has(k)) cache.set(k, v);
    }
    return cache;
  }
  _loadFailureLogged = false;

  for (const row of data) {
    // Supabase returns jsonb as already-parsed JS values
    cache.set(row.key, row.value);
  }

  _version += 1;
  for (const fn of _listeners) { try { fn(_version); } catch { /* a listener never breaks the load */ } }
  return cache;
}

/**
 * The stored value of each key (CONFIG_DEFAULTS only for a key that has no row), for screens where an admin
 * edits settings. THROWS when the database cannot be read, so the screen shows the error and disables saving
 * instead of presenting defaults as if they were stored.
 * @param {string[]} keys
 * @returns {Promise<Object>} { key: value }
 */
export async function loadStoredConfigValues(keys) {
  const cfg = new Map(await loadAllConfigs({ strict: true }));
  const out = {};
  for (const k of keys) out[k] = cfg.has(k) ? cfg.get(k) : CONFIG_DEFAULTS[k];
  return out;
}

// ------------------------------------------------------------
// getConfig(key)
//
// Async. Returns the value for `key`.
// 1. If the cache is populated and contains the key — return it.
// 2. If the cache is empty — trigger a full load first.
// 3. If the key is still missing after load — return CONFIG_DEFAULTS[key].
// 4. If Supabase fails at any point — return CONFIG_DEFAULTS[key].
// Never throws.
// ------------------------------------------------------------
export async function getConfig(key) {
  try {
    // If cache hasn't been loaded yet, do a full load now
    if (!(_cache instanceof Map)) {
      await loadAllConfigs();
    }

    const cache = _ensureCache();

    if (cache.has(key)) {
      return cache.get(key);
    }

    // Key not in cache after full load — try a targeted fetch.
    // maybeSingle() returns null (not a 406) when no row exists yet.
    const { data, error } = await supabase
      .from("platform_config")
      .select("value")
      .eq("key", key)
      .maybeSingle();

    if (error || !data) {
      // Fall back to hardcoded default
      return CONFIG_DEFAULTS[key] ?? null;
    }

    cache.set(key, data.value);
    return data.value;
  } catch (err) {
    console.warn(`[configService] getConfig("${key}") error, using default:`, err);
    return CONFIG_DEFAULTS[key] ?? null;
  }
}

// ------------------------------------------------------------
// setConfig(key, value)
//
// Writes `value` to Supabase for `key` (upsert on the key column),
// then updates the local cache.
//
// Throws if the Supabase write fails (e.g. RLS rejection for
// non-admin callers). Callers should catch and surface the error.
// ------------------------------------------------------------
export async function setConfig(key, value) {
  _assertKnownKey(key);

  // MIN-R5-2 / MIN-R6-3: verify authentication AND admin role before calling the DB.
  // RLS still enforces admin-only at the DB layer, but an explicit check here ensures
  // clear error messages and prevents leaking timing info about key existence.
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error("You must be signed in to change platform settings.");

  const { data: profile } = await supabase
    .from("profiles")
    .select("role")
    .eq("id", user.id)
    .maybeSingle();
  if (!profile || profile.role !== "admin") {
    throw new Error("Admin role required to change platform settings.");
  }

  const normalizedValue = _normalizeValue(key, value);
  const previousValue = await getConfig(key);
  const definition = CONFIG_DEFINITIONS[key];

  const nextValues = { ...CONFIG_DEFAULTS };
  if (_cache instanceof Map) {
    for (const [cacheKey, cacheValue] of _cache.entries()) {
      nextValues[cacheKey] = cacheValue;
    }
  }
  nextValues[key] = normalizedValue;
  _validateRelationships(nextValues);

  // nullable-string fields that resolve to null: delete the row so the DB never
  // receives a null value (platform_config.value has a NOT NULL constraint).
  // The cache stores null and getCachedConfig / getConfig fall back to
  // CONFIG_DEFAULTS[key] (also null) when the row is absent — behaviour is identical.
  if (normalizedValue === null) {
    const { error: delErr } = await supabase
      .from("platform_config")
      .delete()
      .eq("key", key);
    if (delErr && delErr.code !== "PGRST116") {
      // PGRST116 = row not found — safe to ignore
      throw new Error(`Failed to clear ${key}: ${delErr.message}`);
    }
    _ensureCache().set(key, null);
    return; // skip the upsert below
  }

  const { error } = await supabase
    .from("platform_config")
    .upsert(
      {
        key,
        value: normalizedValue,
        label: definition.label,
        description: definition.description ?? null,
        config_group: definition.configGroup,
        updated_at: new Date().toISOString(),
        updated_by: user.id,
      },
      { onConflict: "key" }
    );

  if (error) {
    throw new Error(`Failed to save ${key}: ${error.message}`);
  }

  // Keep cache coherent on success
  _ensureCache().set(key, normalizedValue);

  await logAdminAction({
    actionType: AUDIT.CHANGE_CONFIG,
    targetType: "config",
    targetId: key,
    targetName: key,
    details: {
      old_value: previousValue,
      new_value: normalizedValue,
    },
  });
}

// ------------------------------------------------------------
// getCachedConfig(key)
//
// Synchronous — never throws, never awaits.
// Returns from the in-memory cache if the key is present,
// otherwise returns CONFIG_DEFAULTS[key].
//
// Safe to call anywhere in render logic after loadAllConfigs()
// has resolved. Also works before load (returns defaults).
// ------------------------------------------------------------
export function getCachedConfig(key) {
  if (_cache instanceof Map && _cache.has(key)) {
    return _cache.get(key);
  }
  return CONFIG_DEFAULTS[key] ?? null;
}

// ------------------------------------------------------------
// Convenience re-export so callers can inspect defaults directly
// ------------------------------------------------------------
export { CONFIG_DEFAULTS, CONFIG_DEFINITIONS };
